import { useTranslation } from 'react-i18next';
import { Client, Id } from '@hero/schema';
import { useClientCreateManager, UseClientFormOptions } from '@hero/web-hooks';
import { ModalForm } from '../../../app/Modal';
import { ClientForm } from '../Form/ClientForm';

export type ClientModalMode = 'create' | 'select';

export interface ClientEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after close animation finishes (for deferred unmount). */
  onExited?: () => void;
  /**
   * Callback when a `Client` is saved/created. Receives the freshly
   * created Client; the primary contact is carried inline on
   * `Client.primaryContact` in relations-v2.
   */
  onClientSaved?: (data: { client: Client }) => void;
  /** When set, load and edit this client; when undefined, create mode. */
  clientId?: Id;
  mode?: ClientModalMode;
  allowOutsideClickClose?: boolean;
  /** Optional initial form values when in create mode (e.g. pre-fill roles). */
  defaultValues?: UseClientFormOptions['defaultValues'];
}

/**
 * Edit / create modal for the unified `Client` CRM entity.
 * Replaces `CustomerEditModal`.
 */
export function ClientEditModal({
  isOpen,
  onClose,
  onExited,
  onClientSaved,
  clientId,
  mode = 'create',
  allowOutsideClickClose = true,
  defaultValues,
}: ClientEditModalProps) {
  const { t } = useTranslation(['m-client', 'c-main']);
  const { clientQuery, clientCreate, clientUpdate, formHook, reset, onSaveClick } =
    useClientCreateManager({
      onClientSaved: result => onClientSaved?.({ client: result }),
      onClientUpdated: data => onClientSaved?.({ client: data.client }),
      clientId,
      defaultValues,
    });

  const handleClose = () => {
    reset();
    onClose();
  };

  const isEdit = clientId != null;
  const isLoadingForm = isEdit && clientQuery.isLoading;
  const isLoading = clientCreate.isPending || clientUpdate.isPending;
  const isSuccess = clientCreate.isSuccess || clientUpdate.isSuccess;

  return (
    <ModalForm
      isOpen={isOpen}
      onClose={handleClose}
      onExited={onExited}
      onSave={onSaveClick}
      title={isEdit ? t('modal.edit_client_title') : t('modal.create_client_title')}
      description={
        isEdit ? t('modal.edit_client_description') : t('modal.create_client_description')
      }
      isLoading={isLoading}
      isLoadingForm={isLoadingForm}
      isSuccess={isSuccess}
      allowOutsideClickClose={allowOutsideClickClose && !isLoading}
      hideFooter={false}
      size={isEdit ? 'md' : 'lg'}
      saveText={isEdit ? t('c-main:crud_buttons.update') : t('c-main:crud_buttons.create')}
      disableSave={!formHook.isFormValid}>
      <ClientForm
        formHook={formHook}
        isLoading={isLoading}
        hideSubmitButton={true}
        isEdit={isEdit}
      />
    </ModalForm>
  );
}
